import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Shell } from "../components/Shell";
import { CustomSelect } from "../components/CustomSelect";
import { ApiError, funcionarios, jarvis, tenants } from "../lib/api";
import type {
  AIProviderID,
  FuncionarioOut,
  JarvisSesion,
  PlatformConfig,
  ProviderStatus,
  Tenant,
  TenantAIDraft,
} from "../lib/api";

const PROVIDER_LABEL: Record<AIProviderID, string> = {
  anthropic: "Anthropic Claude",
  openai: "OpenAI ChatGPT",
  gemini: "Google Gemini",
  lm_studio: "LM Studio (local)",
  ollama: "Ollama (local)",
};

export function TenantAIDraftPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [cfg, setCfg] = useState<TenantAIDraft | null>(null);
  const [platform, setPlatform] = useState<PlatformConfig | null>(null);
  const [models, setModels] = useState<{ id: string; label: string }[]>([]);
  const [modelsBusy, setModelsBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([tenants.get(id), tenants.getAIDraft(id), tenants.getPlatformConfig()])
      .then(([t, c, p]) => {
        setTenant(t);
        setCfg(c);
        setPlatform(p);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "No se pudo cargar"))
      .finally(() => setLoading(false));
  }, [id]);

  // Cargar modelos del proveedor seleccionado
  useEffect(() => {
    if (!cfg?.provider) return;
    setModelsBusy(true);
    tenants
      .listProviderModels(cfg.provider)
      .then((r) => setModels(r.models))
      .catch(() => setModels([]))
      .finally(() => setModelsBusy(false));
  }, [cfg?.provider]);

  const update = async (patch: Partial<TenantAIDraft>) => {
    if (!id) return;
    setBusy(true);
    try {
      const c = await tenants.updateAIDraft(id, patch);
      setCfg(c);
      flash("ok", "Guardado");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !tenant || !cfg || !platform) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "Tenant no encontrado."}</div>
          <p>
            <Link to="/tenants">← Volver</Link>
          </p>
        </section>
      </Shell>
    );
  }

  // Solo proveedores configurados en /platform/config son seleccionables
  const availableProviders = platform.providers.filter((p) => p.configured);

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to={`/tenants/${tenant.id}`}>← {tenant.nombre}</Link> · Asistente IA
        </div>
      <h1 style={{ fontSize: 28, marginTop: 6 }}>Borrador automático de respuestas</h1>
      <p className="lede" style={{ maxWidth: 640 }}>
        Cuando el funcionario abre <strong>Redactar respuesta</strong>, el asistente genera
        un borrador formal conforme a la Ley 1755/2015. Elige el proveedor de IA y el
        modelo que prefiera esta alcaldía.
      </p>

      {availableProviders.length === 0 && (
        <div className="alert warn" style={{ marginTop: 18, maxWidth: 640 }}>
          No hay proveedores de IA configurados en la plataforma.{" "}
          <Link to="/platform/config">Configura al menos uno</Link> antes de activar el
          asistente.
        </div>
      )}

      {/* Toggle on/off */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-inner">
          <div className="eyebrow">Estado</div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginTop: 10,
              cursor: availableProviders.length === 0 ? "not-allowed" : "pointer",
              opacity: availableProviders.length === 0 ? 0.5 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={cfg.enabled}
              disabled={busy || availableProviders.length === 0}
              onChange={(e) => void update({ enabled: e.target.checked })}
              style={{ width: 20, height: 20 }}
            />
            <span style={{ fontSize: 15, color: "var(--ink)" }}>
              {cfg.enabled
                ? "Asistente IA activado para esta alcaldía"
                : "Asistente IA desactivado"}
            </span>
          </label>
        </div>
      </div>

      {/* Consentimiento Habeas Data para envío de PII a proveedores cloud */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-inner">
          <div className="eyebrow">Habeas Data · Transferencia internacional</div>
          <p
            className="helper"
            style={{ marginTop: 8, marginBottom: 12, color: "var(--ink-2)" }}
          >
            El borrador IA envía el contenido del PQRSDF y el nombre del
            peticionario al proveedor. Si es cloud (Anthropic / OpenAI /
            Gemini), esos datos personales salen del país — la Ley
            1581/2012 exige consentimiento explícito de la alcaldía. Sin
            esta autorización, el borrador IA con proveedor cloud queda
            bloqueado (los proveedores locales LM Studio / Ollama no
            tienen esta restricción).
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 14,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={cfg.permite_pii_externa}
              disabled={busy}
              onChange={(e) =>
                void update({ permite_pii_externa: e.target.checked })
              }
              style={{ width: 20, height: 20, marginTop: 2 }}
            />
            <span style={{ fontSize: 14, color: "var(--ink)" }}>
              La alcaldía autoriza enviar los datos del PQRSDF a un
              proveedor cloud para generar el borrador con IA, en los
              términos de la política de privacidad publicada.
            </span>
          </label>
        </div>
      </div>

      {/* Proveedor */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-inner">
          <div className="eyebrow">Proveedor de IA</div>
          <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 }}>
            {(["anthropic", "openai", "gemini", "lm_studio", "ollama"] as AIProviderID[]).map(
              (p) => {
                const status = platform.providers.find((x) => x.provider === p) as
                  | ProviderStatus
                  | undefined;
                // El usuario puede elegir provider/modelo aunque el toggle
                // esté en OFF (para preconfigurar). Solo bloqueamos si el
                // proveedor no tiene credenciales o estamos guardando.
                const disabled = !status?.configured || busy;
                return (
                  <li key={p}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        border: "1px solid var(--hairline)",
                        borderRadius: 10,
                        cursor: disabled ? "not-allowed" : "pointer",
                        background:
                          cfg.provider === p ? "var(--bone-warm)" : "var(--paper)",
                        opacity: disabled ? 0.45 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="provider"
                        value={p}
                        checked={cfg.provider === p}
                        disabled={disabled}
                        onChange={() => {
                          // Solo cambia el provider; el usuario elige el modelo
                          // del listado dinámico que se carga después. Pasar
                          // model:"" haría que el backend rechazara la request.
                          void update({ provider: p });
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: "var(--ink)" }}>
                          {PROVIDER_LABEL[p]}
                        </div>
                        <div
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            color: "var(--ink-3)",
                            marginTop: 2,
                          }}
                        >
                          {status?.configured ? "configurado" : "sin credenciales"}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              },
            )}
          </ul>
        </div>
      </div>

      {/* Modelo */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-inner">
          <div className="eyebrow">Modelo</div>
          <p className="helper">
            {cfg.provider === "lm_studio" || cfg.provider === "ollama"
              ? "Modelos cargados en tu endpoint local."
              : "Modelos sugeridos del proveedor."}
          </p>
          {modelsBusy ? (
            <p className="helper">Cargando modelos…</p>
          ) : models.length === 0 ? (
            <input
              type="text"
              className="input"
              value={cfg.model}
              onChange={(e) => void update({ model: e.target.value })}
              placeholder="Escribe el nombre del modelo"
              disabled={busy}
            />
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 }}>
              {models.map((m) => (
                <li key={m.id}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      border: "1px solid var(--hairline)",
                      borderRadius: 10,
                      cursor: busy ? "not-allowed" : "pointer",
                      background: cfg.model === m.id ? "var(--bone-warm)" : "var(--paper)",
                    }}
                  >
                    <input
                      type="radio"
                      name="model"
                      value={m.id}
                      checked={cfg.model === m.id}
                      disabled={busy}
                      onChange={() => void update({ model: m.id })}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: "var(--ink)" }}>{m.label}</div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          color: "var(--ink-3)",
                          marginTop: 2,
                        }}
                      >
                        {m.id}
                      </div>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Asistente por WhatsApp (Jarvis) */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-inner">
          <div className="eyebrow">Asistente por WhatsApp · Jarvis</div>
          <p
            className="helper"
            style={{ marginTop: 8, marginBottom: 12, color: "var(--ink-2)" }}
          >
            Permite que los funcionarios verificados consulten sus PQRSDF y
            redacten borradores escribiéndole al WhatsApp de la alcaldía (texto
            o nota de voz). Usa el <strong>mismo proveedor y modelo de IA</strong>{" "}
            de arriba, así que requiere IA activada y consentimiento de PII
            externa si el proveedor es cloud. Jarvis nunca envía respuestas
            oficiales: solo guarda borradores.
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={cfg.jarvis_enabled}
              disabled={busy}
              onChange={(e) => void update({ jarvis_enabled: e.target.checked })}
              style={{ width: 20, height: 20 }}
            />
            <span style={{ fontSize: 15, color: "var(--ink)" }}>
              {cfg.jarvis_enabled
                ? "Asistente por WhatsApp activado para esta alcaldía"
                : "Asistente por WhatsApp desactivado"}
            </span>
          </label>
          {cfg.jarvis_enabled && (!cfg.enabled || !cfg.permite_pii_externa) && (
            <div className="alert warn" style={{ marginTop: 12 }}>
              Activaste Jarvis, pero para que funcione también debe estar la{" "}
              <strong>IA activada</strong>
              {!cfg.permite_pii_externa && (
                <> y el <strong>consentimiento de PII externa</strong></>
              )}{" "}
              arriba (con un proveedor con credenciales).
            </div>
          )}

          {cfg.jarvis_enabled && (
            <div style={{ marginTop: 16 }}>
              <div className="helper" style={{ marginBottom: 8, color: "var(--ink-2)" }}>
                <strong>¿Sobre qué puede consultar Jarvis?</strong> PQRSDF, resumen y
                Centro de Ayuda están siempre activos. Activa las consultas de operación
                que quieras habilitar (requieren tener el módulo correspondiente activo):
              </div>
              {(
                [
                  ["citas", "Citas / turnos del día"],
                  ["expedientes", "Expedientes del archivo"],
                  ["correspondencia", "Correspondencia (oficios)"],
                  ["actos", "Actos administrativos"],
                  ["acciones", "Acciones (ej. marcar radicado En proceso) — con confirmación"],
                ] as const
              ).map(([key, label]) => (
                <label
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "6px 0",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(cfg.jarvis_capacidades?.[key])}
                    disabled={busy}
                    onChange={(e) =>
                      void update({
                        jarvis_capacidades: {
                          ...cfg.jarvis_capacidades,
                          [key]: e.target.checked,
                        },
                      })
                    }
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: 14, color: "var(--ink)" }}>{label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <JarvisVinculos tenantId={tenant.id} />

        {toast && <div className={`toast ${toast.kind}`} role="status" aria-live="polite">{toast.msg}</div>}
      </section>
    </Shell>
  );
}

function JarvisVinculos({ tenantId }: { tenantId: string }) {
  const [sesiones, setSesiones] = useState<JarvisSesion[]>([]);
  const [funcs, setFuncs] = useState<FuncionarioOut[]>([]);
  const [sel, setSel] = useState("");
  const [tel, setTel] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Funcionarios que aún no tienen número vinculado.
  const disponibles = funcs.filter(
    (f) => !sesiones.some((s) => s.usuario_tenant_id === f.id),
  );

  const load = async () => {
    try {
      const [s, f] = await Promise.all([
        jarvis.listSesiones(tenantId),
        funcionarios.list(tenantId),
      ]);
      setSesiones(s);
      setFuncs(f.items.filter((x) => x.activo));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudieron cargar los vínculos");
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const enrolar = async () => {
    if (!sel || tel.trim().length < 8) {
      setError("Elige un funcionario y escribe su número con indicativo (+57…).");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const r = await jarvis.enrolar(tenantId, { usuario_tenant_id: sel, telefono: tel.trim() });
      setInfo(
        r.otp_enviado
          ? "Código enviado. El funcionario debe responder ese código por WhatsApp para confirmar."
          : r.detalle || "No se pudo enviar el código.",
      );
      setSel("");
      setTel("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const eliminar = async (id: string) => {
    if (!window.confirm("¿Desvincular este número del asistente?")) return;
    setBusy(true);
    try {
      await jarvis.eliminar(tenantId, id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-inner">
        <div className="eyebrow">Vínculos de WhatsApp · Jarvis</div>
        <p className="helper" style={{ marginTop: 8, marginBottom: 12, color: "var(--ink-2)" }}>
          Vincula el número de WhatsApp de cada funcionario. Se le envía un código
          por WhatsApp; el funcionario lo confirma <strong>respondiendo ese código
          por el mismo chat</strong> (no necesita entrar al panel).
        </p>

        {error && <div className="alert err" style={{ marginBottom: 10 }}>{error}</div>}
        {info && <div className="alert ok" style={{ marginBottom: 10 }}>{info}</div>}

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <label className="label" style={{ fontSize: 12 }}>Funcionario</label>
            <CustomSelect
              value={sel}
              onChange={setSel}
              disabled={busy}
              placeholder={disponibles.length ? "Selecciona un funcionario…" : "Todos vinculados"}
              aria-label="Funcionario a vincular"
              options={disponibles.map((f) => ({
                value: f.id,
                label: f.nombre,
                hint: f.rol,
              }))}
            />
          </div>
          <div style={{ flex: "1 1 180px" }}>
            <label className="label" style={{ fontSize: 12 }}>Número de WhatsApp</label>
            <input
              className="input"
              value={tel}
              onChange={(e) => setTel(e.target.value)}
              placeholder="+57 300 000 0000"
              disabled={busy}
            />
          </div>
          <button
            type="button"
            className="cta"
            style={{ width: "auto" }}
            onClick={() => void enrolar()}
            disabled={busy || !sel || tel.trim().length < 8}
          >
            <span className="cta-inner">Enviar código</span>
          </button>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 18 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--hairline)" }}>
              <th className="helper" style={{ padding: "6px 4px" }}>Funcionario</th>
              <th className="helper" style={{ padding: "6px 4px" }}>Número</th>
              <th className="helper" style={{ padding: "6px 4px" }}>Estado</th>
              <th className="helper" style={{ padding: "6px 4px" }}></th>
            </tr>
          </thead>
          <tbody>
            {sesiones.length === 0 ? (
              <tr>
                <td colSpan={4} className="helper" style={{ padding: "14px 4px", color: "var(--ink-3)" }}>
                  Aún no hay números vinculados.
                </td>
              </tr>
            ) : (
              sesiones.map((s) => (
                <tr key={s.id} style={{ borderBottom: "1px solid var(--hairline)" }}>
                  <td style={{ padding: "8px 4px" }}>
                    <div style={{ fontWeight: 600 }}>{s.funcionario_nombre}</div>
                    <div className="helper" style={{ color: "var(--ink-3)" }}>{s.funcionario_email}</div>
                  </td>
                  <td style={{ padding: "8px 4px", fontFamily: "var(--font-mono)", fontSize: 13 }}>
                    {s.telefono ?? "—"}
                  </td>
                  <td style={{ padding: "8px 4px" }}>
                    {s.verificado ? (
                      <span style={{ color: "var(--ok)", fontWeight: 600 }}>● Verificado</span>
                    ) : s.pendiente ? (
                      <span style={{ color: "var(--warn)", fontWeight: 600 }}>● Pendiente (OTP)</span>
                    ) : (
                      <span style={{ color: "var(--ink-3)" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 4px", textAlign: "right" }}>
                    <button
                      type="button"
                      className="btn-outline"
                      style={{ padding: "4px 10px", fontSize: 12, color: "var(--err)" }}
                      onClick={() => void eliminar(s.id)}
                      disabled={busy}
                    >
                      Desvincular
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
